import {
    DataPlatformApiClient,
    DataPlatformApiEnvironment,
} from '@kcftech/data-platform-sdk';
import { Client } from '@kcftech/prometheustypescriptapiclient';
import { Auth } from 'auth-provider';
import { IUserContext } from 'contexts/UserContext';
import { logout } from 'pages/Login/Login';
import { sentryAddBreadcrumb, sentrySetContext } from 'utils/sentry';

const getHeaders = (
    accessToken: string,
    corporationId: string | undefined,
    locationId: string | undefined,
    options?: RequestInit
): Headers => {
    const headers = new Headers(options?.headers);
    // Checking for form data here to indicate a file is being uploaded.
    // Rest of our app does not use FormData so we assume here.
    // Do not set content type when uploading file. Browser will take care of this.
    if (!(options?.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('x-KCF-corp_id', corporationId ?? '');
    headers.set('x-KCF-location_id', locationId ?? '');
    return headers;
};

const getDataPlatformEnvironment = (): DataPlatformApiEnvironment => {
    const env = process.env.KCF_ENV;
    switch (env) {
        case 'stage01':
            return DataPlatformApiEnvironment.Nonprod;
        case 'stage02':
            return DataPlatformApiEnvironment.Preprod;
        case 'prod':
            return DataPlatformApiEnvironment.Prod;
        default:
            return DataPlatformApiEnvironment.Nonprod;
    }
};

export class ClientHelper {
    public sdClient: Client;
    public dataPlatformClient: DataPlatformApiClient;

    public sdClientMetadata: { PrometheusApiVersion: string | undefined } = {
        PrometheusApiVersion: undefined,
    };

    constructor() {
        const api = process.env.REACT_APP_API_BASE_URL;
        this.sdClient = new Client(api, {
            fetch: async (url: RequestInfo, init?: RequestInit) => {
                const controller = new AbortController();
                const urlString = url instanceof Request ? url.url : url;

                const endpointPath = urlString
                    .replace(api ?? '', '') // eslint-disable-line @typescript-eslint/no-unnecessary-condition -- env var may be undefined at runtime
                    .split('?')[0]
                    .slice(0, 100);

                try {
                    const response = await fetch(url, {
                        ...init,
                        signal:
                            // global mocks introduce an incompatibility with nodejs internals, it seems
                            process.env.NODE_ENV !== 'test'
                                ? controller.signal
                                : undefined,
                    });

                    this.sdClientMetadata.PrometheusApiVersion =
                        response.headers.get('prom-svc-version') ?? undefined;

                    if (response.status === 401) {
                        logout();
                    }

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- abort support; unreachable from callers due to async wrapper but kept for SDK contract
                    (response as any).cancel = () => controller.abort();

                    return response;
                } catch (error) {
                    sentryAddBreadcrumb({
                        category: 'network.error',
                        message: `API request failed: ${endpointPath}`,
                        level: 'warning',
                        data: {
                            endpoint: endpointPath,
                            method: init?.method ?? 'GET',
                            online: navigator.onLine,
                            errorMessage:
                                error instanceof Error ? error.message : String(error),
                        },
                    });

                    sentrySetContext('lastFetchError', {
                        endpoint: endpointPath,
                        method: init?.method ?? 'GET',
                        timestamp: new Date().toISOString(),
                        online: navigator.onLine,
                    });

                    throw error;
                }
            },
        });
        this.addHeaders(undefined, undefined, undefined);

        this.dataPlatformClient = new DataPlatformApiClient({
            environment: getDataPlatformEnvironment(),
            token: getTokenRequired,
        });
    }

    addHeaders = (corporationId?: string, locationId?: string, user?: IUserContext) => {
        this.sdClient.transformOptions = getTransformOptions(
            corporationId,
            locationId,
            user
        );
    };
}

const getTransformOptions = (
    corporationId?: string,
    locationId?: string,
    user?: IUserContext
) => async (options: RequestInit): Promise<RequestInit> => {
    const token = await getToken(user);

    if (!token) {
        // If there is no token, we can assume the user is not authenticated
        return options;
    }

    return {
        ...options,
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: getHeaders(token, corporationId, locationId, options),
    };
};

const getToken = async (user?: IUserContext): Promise<string | undefined> => {
    // NOTE: The reason for calling currentSession() is because we want
    // to check that the token is still valid, for example, if it has
    // expired or no longer exists. If the token is expired but can be
    // refreshed, then that will happen implicitly in the call to currentSession().
    // We only reject if an exception is thrown (i.e., could not
    // reauthenticate to the server).
    try {
        const session = await Auth.currentSession();
        return session.getAccessToken().getJwtToken();
    } catch (err) {
        // We may not have an authenticated session (e.g., logging in and
        // need to call auth APIs), and calling Auth.currentSession() will
        // not find the session, so Amplify will throw an exception. If the
        // user was not considered authenticated then, it's okay to just
        // resolve the promise. If the user was already authenticated and we
        // got an error, then something is wrong so we reject the promise.
        if (!user?.authenticated) {
            return undefined;
        }
        throw err;
    }
};

const getTokenRequired = async (user?: IUserContext): Promise<string> => {
    const token = await getToken(user);
    if (!token) {
        throw new Error('No access token available');
    }
    return token;
};
